/**
 * Curved Horizontal Multi-Screen Project Slider
 * Features:
 * - 1:1 responsive pointer dragging with friction-based momentum inertia glide
 * - Seamless smooth snapping to nearest preview card
 * - Transparent container with frosted pill pagination navigator
 * - Instant dot click jump and real-time active dot updates
 * - Lightbox modal trigger on click without drag interference
 */

import { LightboxModal } from './lightbox-modal.js';

export class CurvedProjectSlider {
  constructor(selector = '.curved-slider-container') {
    this.containers = document.querySelectorAll(selector);
    this.init();
  }

  init() {
    this.containers.forEach((container) => {
      if (container.dataset.sliderInit === 'true') return;
      container.dataset.sliderInit = 'true';

      const track = container.querySelector('.curved-slider-track');
      const dots = container.querySelectorAll('.curved-slider-dot');
      if (!track) return;

      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let dragDistance = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      let momentumRafId = null;
      let isMomentumActive = false;

      const getClosestSlideIndex = () => {
        const screens = track.querySelectorAll('.curved-screen-item');
        if (!screens.length) return 0;

        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll > 0 && track.scrollLeft >= maxScroll - 20) {
          return screens.length - 1;
        }

        const trackLeft = track.getBoundingClientRect().left;
        let closestIdx = 0;
        let minDiff = Infinity;
        screens.forEach((screen, idx) => {
          const diff = Math.abs(screen.getBoundingClientRect().left - trackLeft);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = idx;
          }
        });
        return closestIdx;
      };

      const updateDots = () => {
        if (!dots.length) return;
        const closestIdx = getClosestSlideIndex();
        dots.forEach((dot, idx) => {
          if (idx === closestIdx) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      };

      const cancelMomentum = () => {
        if (momentumRafId) {
          cancelAnimationFrame(momentumRafId);
          momentumRafId = null;
        }
        isMomentumActive = false;
      };

      const snapToSlide = (idx) => {
        const screens = track.querySelectorAll('.curved-screen-item');
        if (!screens[idx]) return;

        // Immediate visual dot feedback
        dots.forEach((dot, i) => {
          if (i === idx) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });

        const screenRect = screens[idx].getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        const target = track.scrollLeft + (screenRect.left - trackRect.left);

        track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        setTimeout(updateDots, 400);
      };

      // Prevent native browser image drag
      track.addEventListener('dragstart', (e) => e.preventDefault());

      let isDragging = false;
      let pointerDownTarget = null;

      // Pointer Event Handlers for Mouse & Touch Unification
      track.addEventListener('pointerdown', (e) => {
        // Only trigger on primary button
        if (e.button !== 0 && e.pointerType === 'mouse') return;

        cancelMomentum();
        isDown = true;
        isDragging = false;
        pointerDownTarget = e.target.closest('.curved-screen-item');
        startX = e.clientX;
        startScrollLeft = track.scrollLeft;
        dragDistance = 0;
        lastX = e.clientX;
        lastTime = window.performance ? window.performance.now() : Date.now();
        velocity = 0;

        try {
          track.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      });

      track.addEventListener('pointermove', (e) => {
        if (!isDown) return;

        const deltaX = e.clientX - startX;
        dragDistance = Math.max(dragDistance, Math.abs(deltaX));

        // Only enter drag mode if moved beyond intentional click jitter threshold (6px)
        if (!isDragging && Math.abs(deltaX) > 6) {
          isDragging = true;
          track.classList.add('is-dragging');
        }

        if (!isDragging) return;

        // 1:1 Direct tracking without unnatural multiplier
        track.scrollLeft = startScrollLeft - deltaX;

        const now = window.performance ? window.performance.now() : Date.now();
        const dt = now - lastTime;
        if (dt > 10) {
          velocity = (e.clientX - lastX) / dt;
          lastX = e.clientX;
          lastTime = now;
        }

        updateDots();
      });

      const handlePointerEnd = (e) => {
        if (!isDown) return;
        isDown = false;

        try {
          if (track.hasPointerCapture(e.pointerId)) {
            track.releasePointerCapture(e.pointerId);
          }
        } catch {
          // ignore
        }

        if (isDragging && dragDistance > 6) {
          track.classList.remove('is-dragging');
          isDragging = false;

          const screens = track.querySelectorAll('.curved-screen-item');
          let targetIdx = getClosestSlideIndex();

          const deltaX = e.clientX - startX;
          if (deltaX < -25 || velocity < -0.15) {
            targetIdx = Math.min(screens.length - 1, targetIdx + 1);
          } else if (deltaX > 25 || velocity > 0.15) {
            targetIdx = Math.max(0, targetIdx - 1);
          }

          snapToSlide(targetIdx);
        } else {
          track.classList.remove('is-dragging');
          isDragging = false;
        }
      };

      track.addEventListener('pointerup', handlePointerEnd);
      track.addEventListener('pointercancel', handlePointerEnd);

      // Lightbox modal opener helper
      const openLightboxForScreen = (screen) => {
        if (!screen) return;
        const img = screen.querySelector('img');
        if (!img) return;

        // Parse images belonging to this project from data-images
        let images = [];
        const rawImages = screen.getAttribute('data-images');
        if (rawImages) {
          try {
            images = JSON.parse(rawImages);
          } catch (err) {
            console.error('Failed to parse project images', err);
          }
        }
        if (!images.length) {
          const siblingScreens = Array.from(track.querySelectorAll('.curved-screen-item'));
          if (siblingScreens.length > 0) {
            images = siblingScreens
              .map((s) => {
                const sImg = s.querySelector('img');
                return { url: sImg?.src || '', alt: sImg?.alt || '' };
              })
              .filter((i) => i.url);
          }
        }
        if (!images.length) {
          images = [{ url: img.src, alt: img.alt || '' }];
        }

        const projectContainer = screen.closest('.space-y-8');
        const projectLink = projectContainer?.querySelector('a.nbk-pill-btn');
        const projectTitle = projectContainer?.querySelector('h3');

        const title =
          screen.getAttribute('data-title') ||
          projectTitle?.textContent?.trim() ||
          img.alt ||
          'Project Preview';
        const link = screen.getAttribute('data-link') || projectLink?.href || '';
        const btnText = screen.getAttribute('data-btn-text') || 'VIEW CASE STUDY';
        const index = parseInt(screen.getAttribute('data-index') || '0', 10);

        if (!window.nbkLightbox) {
          window.nbkLightbox = new LightboxModal();
        }

        if (window.nbkLightbox) {
          window.nbkLightbox.open({
            images,
            index,
            title,
            link,
            btnText,
          });
        }
      };

      // Direct click listeners on all screen items inside track
      const screenItems = track.querySelectorAll('.curved-screen-item');
      screenItems.forEach((screen) => {
        screen.addEventListener('click', (e) => {
          if (isDragging || dragDistance > 8) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          openLightboxForScreen(screen);
        });
      });

      // Delegated click fallback on track
      track.addEventListener('click', (e) => {
        if (isDragging || dragDistance > 8) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const screen = e.target.closest('.curved-screen-item') || pointerDownTarget;
        if (screen) {
          openLightboxForScreen(screen);
        }
      });

      // Passive scroll listener for native touch swipe & wheel
      let scrollDebounce = null;
      track.addEventListener(
        'scroll',
        () => {
          if (!isDown && !isMomentumActive) {
            if (scrollDebounce) cancelAnimationFrame(scrollDebounce);
            scrollDebounce = requestAnimationFrame(() => {
              updateDots();
            });
          }
        },
        { passive: true },
      );

      // Dot click handler for instant smooth navigation
      dots.forEach((dot, idx) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          cancelMomentum();
          track.classList.remove('is-dragging');
          snapToSlide(idx);
        });
      });

      // Initial dot state
      updateDots();
    });
  }
}
